import { useState } from 'react'
import { Report } from '@/core/models/report.model'
import { Patient } from '@/core/models/patient.model'
import { useReportLicenseEnabled } from '@/features/report'
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Input,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Pagination,
} from '@mui/material'
import { t, Trans } from '@lingui/macro'
import EditReportModal from './EditReportModal'
import DeleteReportModal from './DeleteReportModal'
import { ChipDate } from '@/components/ui/buttons/ChipButton/Chipdate'
import { ReportTemplate } from '@/core/models/report.model'

export function ReportsViewer({ reports }: { reports: Report[] }) {
  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const rowsPerPage = 10
  const hasReportLicence = useReportLicenseEnabled()

  const handleChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)
  }

  const searchValue = searchTerm.toLowerCase().trim()
  const filteredReports = reports.filter((report) => {
    const patient = report.patient as Patient | undefined
    const firstName = patient?.userInfo?.name?.toLowerCase()?.trim() ?? ''
    const lastName = patient?.userInfo?.surname?.toLowerCase()?.trim() ?? ''
    return firstName.includes(searchValue) || lastName.includes(searchValue)
  })

  const startIndex = (page - 1) * rowsPerPage
  const paginatedReports = filteredReports.slice(startIndex, startIndex + rowsPerPage)
  const pageCount = Math.ceil(filteredReports.length / rowsPerPage)

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">{t`Available reports`}</Typography>
      </Box>

      <FormControl sx={{ width: '50vh', mb: 1 }} color="primary">
        <InputLabel htmlFor="search">{t`Search`}</InputLabel>
        <Input
          id="search"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
            setPage(1)
          }}
          aria-describedby="my-helper-search"
        />
      </FormControl>

      <TableContainer
        component={Paper}
        sx={{
          height: 500,
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: 'background.paper',
              }}
            >
              <TableCell>
                <Trans>Template</Trans>
              </TableCell>
              <TableCell>
                <Trans>Patient</Trans>
              </TableCell>
              <TableCell>
                <Trans>Creation date</Trans>
              </TableCell>
              <TableCell>
                <Trans>Actions</Trans>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedReports.length > 0 ? (
              paginatedReports.map((report) => {
                const patient = report.patient as Patient | undefined
                const firstName = patient?.userInfo?.name?.trim() ?? ''
                const lastName = patient?.userInfo?.surname?.trim() ?? ''
                const fullName = `${firstName} ${lastName}`.trim()

                return (
                  <TableRow key={report._id}>
                    <TableCell>{(report.template as ReportTemplate)?.title ?? t`Unknown template`}</TableCell>
                    <TableCell>{fullName || t`Unknown patient`}</TableCell>
                    <TableCell>
                      <ChipDate date={report.createdAt} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={2}>
                        {hasReportLicence && <EditReportModal reportId={report._id} />}
                        {hasReportLicence && <DeleteReportModal reportId={report._id} />}
                      </Stack>
                    </TableCell>
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {t`No report available`}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack spacing={2} sx={{ mt: 2, alignItems: 'center' }}>
        <Pagination count={pageCount} page={page} variant="outlined" onChange={handleChange} shape="rounded" showFirstButton showLastButton />
      </Stack>
    </Box>
  )
}
